import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row
} from 'antd'
import { ModalProps } from 'antd/lib'
import { useEffect } from 'react'

import {
  useStocksServiceCreateActiveSubstanceEndpoint,
  useStocksServiceGetActiveSubstanceEndpoint,
  useStocksServiceGetActiveSubstanceListEndpointKey,
  useStocksServiceUpdateActiveSubstanceEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

export const ActiveSubstanceModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const { data, isLoading } = useStocksServiceGetActiveSubstanceEndpoint(
    {
      id: String(id)
    },
    [id],
    { enabled: !!id }
  )

  const { mutate: create } = useStocksServiceCreateActiveSubstanceEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetActiveSubstanceListEndpointKey]
      })
      message.success(t('active_substances.create_substance_success'))
      onCancel()
    }
  })

  const { mutate, isPending } = useStocksServiceUpdateActiveSubstanceEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetActiveSubstanceListEndpointKey]
      })
      message.success(t('common.save_success'))
      onCancel()
    }
  })

  const onSave = async () => {
    const values = await form.validateFields()

    const payload = {
      name: values?.name,
      prefix: values?.prefix,
      order: values?.order
    }

    id
      ? mutate({ id: String(id), requestBody: payload })
      : create({
          requestBody: payload
        })
  }

  useEffect(() => {
    if (data)
      form.setFieldsValue({
        name: data?.name,
        prefix: data?.prefix,
        order: data?.order
      })
  }, [data])

  return (
    <Modal
      open
      centered
      loading={isLoading}
      width={700}
      footer={null}
      maskClosable={false}
      title={t(id ? 'common.edit' : 'active_substances.create_substance')}
      onCancel={onCancel}
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t('classifiers.title')}
              rules={requiredRules}
              className="full-width"
            >
              <Input placeholder={t('common.enter_text')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="prefix" label={t('active_substances.prefix')}>
              <Input placeholder={t('active_substances.prefix')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="order" label={t('common.order')}>
              <InputNumber
                className="full-width"
                placeholder={t('common.order')}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${t('common.save')}?`}
                onConfirm={onSave}
                okButtonProps={{ loading: isPending }}
              >
                <Button type="primary">{t('common.save')}</Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
